<template>
    <div
        class="sf__drawer-flow"
        :class="[
            `sf__drawer-flow--${currentStep}`,
            {
                'sf__drawer-flow--fitment-bypass-loading': fitmentBypassLoading,
                'sf__drawer-flow--fitment-bypassed': fitmentBypassed,
                'sf__drawer-flow--full-configuration-bypassed': fullConfigurationBypassed,
                'sf__drawer-flow--material-bypassed': materialBypassed
            }
        ]"
    >
        <ol class="sf__drawer-flow__steps" aria-label="Vehicle search progress">
            <template v-for="(step, index) in steps" :key="step.key">
                <li
                    class="sf__drawer-flow__step"
                    :class="stepClass(step.key)"
                    :aria-disabled="isSkippedStep(step.key).toString()"
                    :aria-describedby="isSkippedFitmentStep(step.key) ? 'sf-drawer-flow-skipped-fitment-tooltip' : null"
                    :ref="el => setSkippedFitmentStepRef(el, step.key)"
                    @mouseenter="openSkippedFitmentTooltip(step.key)"
                    @mouseleave="closeSkippedFitmentTooltip"
                    @focusin="openSkippedFitmentTooltip(step.key)"
                    @focusout="closeSkippedFitmentTooltip"
                >
                    <span class="sf__drawer-flow__step-number">{{ index + 1 }}</span>
                    <span>{{ stepLabel(step) }}</span>
                </li>
                <li v-if="index < steps.length - 1" class="sf__drawer-flow__step-separator" aria-hidden="true">›</li>
            </template>
        </ol>

        <div
            class="sf__drawer-flow__content"
            @click="dismissFitmentSkippedToastOnMaterialInteraction"
            @scroll.passive="dismissFitmentSkippedToastOnMaterialInteraction"
        >
            <section v-if="currentStep === 'vehicle'" class="sf__drawer-flow__vehicle">
                <div class="sf__drawer-flow__intro">
                    <div class="sf__drawer-flow__title-row">
                        <h2 class="sf__drawer-flow__title">Select your vehicle:</h2>
                    </div>
                </div>

                <Vehicles
                    :key="vehicleKey"
                    :foam="foam"
                    :modal-flow="true"
                    :initial-selections="seedVehicleSelection"
                    button-text="Next: Fitment"
                    :year-label="yearLabel || 'Year'"
                    :make-label="makeLabel || 'Make'"
                    :model-label="modelLabel || 'Select your vehicle model...'"
                    :submodel-label="submodelLabel || 'Select your vehicle sub-model...'"
                    :processing="fitmentBypassLoading"
                    @vehicleSelected="handleVehicleSelected"
                />

                <p v-if="error" class="sf__drawer-flow__error">{{ error }}</p>

                <div class="sf__drawer-flow__footer">
                    <button type="button" class="sf__drawer-flow__back" @click="closeDrawer">
                        <span class="sf__drawer-flow__arrow">‹</span>
                        Back
                    </button>
                </div>
            </section>

            <div v-else-if="loading" class="sf__drawer-flow__loading">
                <Loader />
            </div>

            <Filters
                v-else-if="results.length && vehicle"
                :key="filtersKey"
                :global-selections="selections"
                :foam="isFoam()"
                :vehicle="vehicle"
                :popup="true"
                :results="results"
                :drawer-flow="true"
                :initial-step="currentStep"
                :prepared-filters="preparedFilters"
                :fitment-bypassed="fitmentBypassed"
                :full-configuration-bypassed="fullConfigurationBypassed"
                @onApply="viewProducts"
                @drawerStepChange="setCurrentStep"
                @drawerBack="goBack"
                @closeDrawer="closeDrawer"
            />
        </div>
        <transition name="fade">
            <div v-if="showFitmentSkippedToast" class="sf__drawer-flow__toast" role="status">
                <span class="sf__drawer-flow__toast-icon" aria-hidden="true">i</span>
                <div class="sf__drawer-flow__toast-content">
                    <strong>{{ filterAppMessage('skippedFitmentHeading') }}</strong>
                    <p>{{ filterAppMessage('skippedFitmentDescription') }}</p>
                </div>
                <button
                    type="button"
                    class="sf__drawer-flow__toast-close"
                    aria-label="Dismiss"
                    @click="dismissFitmentSkippedToast"
                >
                    ×
                </button>
            </div>
        </transition>
        <teleport to="body">
            <transition name="fade">
                <div
                    v-if="skippedFitmentTooltipOpen"
                    id="sf-drawer-flow-skipped-fitment-tooltip"
                    ref="skippedFitmentTooltip"
                    class="sf__drawer-flow__skipped-tooltip sf__drawer-flow__skipped-tooltip--floating"
                    role="tooltip"
                    :style="skippedFitmentTooltipStyle"
                >
                    {{ filterAppMessage('skippedFitmentTooltip') }}
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
import Filters from './Filters.vue';
import Loader from './Loader.vue';
import Result from '../models/Result';
import Vehicle from '../models/Vehicle';
import Vehicles from './Vehicles.vue';
import Storage from '../helpers/storage';
import Searcher from '../helpers/searcher';
import FilterBuilder from '../helpers/filters';
import {slugify} from '../helpers/helpers';
import {getFilterAppMessage} from '../helpers/filterAppMessages';
import {
    FITMENT_KEYS,
    filterSelectionsForStep,
    storeCurrentSelection,
    clearCurrentSelection
} from '../helpers/currentSelection';

export default {
    props: [
        'foam',
        'yearLabel',
        'makeLabel',
        'modelLabel',
        'submodelLabel'
    ],

    components: {
        Filters,
        Loader,
        Vehicles
    },

    data() {
        return {
            currentStep: 'vehicle',
            error: null,
            fitmentBypassed: false,
            fitmentBypassLoading: false,
            fullConfigurationBypassed: false,
            fitmentSkippedToastTimer: null,
            filtersKey: 0,
            ignored: [],
            loading: false,
            materialBypassed: false,
            preparedFilters: null,
            results: [],
            seedVehicleSelection: null,
            selections: {},
            skippedFitmentStepEl: null,
            skippedFitmentTooltipOpen: false,
            skippedFitmentTooltipStyle: {},
            vehicleKey: 0,
            vehicle: null,
            vehicleHandle: null,
            vehicleParams: null,
            showFitmentSkippedToast: false,
            steps: [
                { key: 'vehicle', label: 'Vehicle' },
                { key: 'fitment', label: 'Fitment' },
                { key: 'material', label: 'Material' }
            ]
        };
    },

    mounted() {
        document.addEventListener('seatshop:vehicle-drawer-close', this.resetFlow);
        document.addEventListener('seatshop:vehicle-drawer-open', this.handleDrawerOpen);
        document.addEventListener('seatshop:vehicle-drawer-back', this.goBack);
        window.addEventListener('resize', this.positionSkippedFitmentTooltip);
        window.addEventListener('scroll', this.positionSkippedFitmentTooltip, true);
    },

    beforeUnmount() {
        this.clearFitmentSkippedToastTimer();
        document.removeEventListener('seatshop:vehicle-drawer-close', this.resetFlow);
        document.removeEventListener('seatshop:vehicle-drawer-open', this.handleDrawerOpen);
        document.removeEventListener('seatshop:vehicle-drawer-back', this.goBack);
        window.removeEventListener('resize', this.positionSkippedFitmentTooltip);
        window.removeEventListener('scroll', this.positionSkippedFitmentTooltip, true);
    },

    methods: {
        filterAppMessage: getFilterAppMessage,

        stepLabel(step) {
            return step.label;
        },

        isSkippedFitmentStep(step) {
            return !this.fullConfigurationBypassed
                && this.fitmentBypassed
                && step === 'fitment';
        },

        isSkippedStep(step) {
            if (this.fullConfigurationBypassed) {
                return ['fitment', 'material'].includes(step);
            }

            if (this.materialBypassed && step === 'material') {
                return true;
            }

            return this.isSkippedFitmentStep(step);
        },

        setSkippedFitmentStepRef(el, step) {
            if (step === 'fitment') {
                this.skippedFitmentStepEl = el;
            }
        },

        openSkippedFitmentTooltip(step) {
            if (!this.isSkippedFitmentStep(step)) return;

            this.skippedFitmentTooltipOpen = true;
            this.$nextTick(this.positionSkippedFitmentTooltip);
        },

        closeSkippedFitmentTooltip() {
            this.skippedFitmentTooltipOpen = false;
        },

        positionSkippedFitmentTooltip() {
            if (!this.skippedFitmentTooltipOpen || !this.skippedFitmentStepEl) return;

            const rect = this.skippedFitmentStepEl.getBoundingClientRect();
            const viewportPadding = 8;
            const width = Math.min(220, window.innerWidth - viewportPadding * 2);
            const panelHeight = this.$refs.skippedFitmentTooltip?.getBoundingClientRect().height || 32;
            const left = Math.min(
                Math.max(viewportPadding, rect.left + (rect.width / 2) - (width / 2)),
                window.innerWidth - width - viewportPadding
            );
            let top = rect.top - panelHeight - 10;

            if (top < viewportPadding) {
                top = rect.bottom + 10;
            }

            this.skippedFitmentTooltipStyle = {
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`
            };
        },

        stepClass(step) {
            if (this.fitmentBypassLoading) {
                return {
                    'is-complete': step === 'vehicle',
                    'is-loading': step === 'fitment'
                };
            }

            if (this.isSkippedFitmentStep(step)) {
                return {
                    'is-complete': true,
                    'is-skipped': true
                };
            }

            if (this.fullConfigurationBypassed && ['fitment', 'material'].includes(step)) {
                return {
                    'is-complete': true,
                    'is-skipped': true
                };
            }

            if (this.materialBypassed && step === 'material') {
                return {
                    'is-complete': true,
                    'is-skipped': true
                };
            }

            const current = this.steps.findIndex(item => item.key === this.currentStep);
            const index = this.steps.findIndex(item => item.key === step);

            return {
                'is-active': index === current,
                'is-complete': index < current
            };
        },

        async handleVehicleSelected(data) {
            this.error = null;
            this.loading = false;
            this.fitmentBypassLoading = true;
            this.fitmentBypassed = false;
            this.fullConfigurationBypassed = false;
            this.materialBypassed = false;
            this.preparedFilters = null;
            this.dismissFitmentSkippedToast();

            try {
                const hits = data.hits || [];

                if (!hits.length) {
                    this.error = 'No matching products were found for that vehicle.';
                    this.fitmentBypassLoading = false;
                    return;
                }

                this.vehicleParams = data.params;
                this.vehicleHandle = data.handle;
                this.seedVehicleSelection = {...data.params};
                this.results = hits.map(hit => new Result(hit, new Vehicle(hit.vehicle)));
                this.vehicle = this.results[0].vehicle;
                this.selections = {};
                this.ignored = [];

                this.preparedFilters = await this.buildPreparedFilters();

                if (!this.hasConfigurableFitment(this.preparedFilters)) {
                    if (!this.hasConfigurableMaterial(this.preparedFilters)) {
                        this.fitmentBypassed = true;
                        this.fullConfigurationBypassed = true;
                        this.materialBypassed = true;
                        this.fitmentBypassLoading = false;
                        this.currentStep = 'material';
                        this.filtersKey++;
                        return;
                    }

                    this.fitmentBypassed = true;
                    this.fitmentBypassLoading = false;
                    this.currentStep = 'material';
                    this.filtersKey++;
                    this.showFitmentSkippedToastMessage();
                    return;
                }

                this.fitmentBypassLoading = false;
                this.currentStep = 'fitment';
                this.filtersKey++;
            } catch (error) {
                this.error = 'We could not load the available options for that vehicle. Please try again.';
                this.fitmentBypassLoading = false;
            } finally {
                this.loading = false;
            }
        },

        async buildPreparedFilters() {
            return (new FilterBuilder(this.results)).build({
                vehicle_id: this.vehicle.id
            });
        },

        hasConfigurableFitment(filters) {
            const fitmentSlugs = ['cab_configuration', 'seat_configuration_1'];

            return filters.some(filter => {
                if (!fitmentSlugs.includes(filter.slug)) {
                    return false;
                }

                return this.activeFilterValues(filter).length > 1;
            });
        },

        hasConfigurableMaterial(filters, selections = {}) {
            const fitmentSelections = FITMENT_KEYS.reduce((values, key) => {
                const value = selections[key];

                if (![null, '', undefined].includes(value)) {
                    values[key] = value;
                }

                return values;
            }, {});

            return filters.some(filter => {
                return filter.type === 'part'
                    && this.activeFilterValues(filter, fitmentSelections).length > 1;
            });
        },

        activeFilterValues(filter, selections = {}) {
            return filter.values.filter(value => {
                return this.results.some(result => {
                    const matchesSelections = Object.keys(selections).every(key => {
                        return result.optionMatches(key, selections[key]);
                    });

                    return matchesSelections && result.optionMatches(filter.slug, value.value);
                });
            });
        },

        showFitmentSkippedToastMessage() {
            this.clearFitmentSkippedToastTimer();
            this.showFitmentSkippedToast = true;
            this.fitmentSkippedToastTimer = window.setTimeout(() => {
                this.showFitmentSkippedToast = false;
                this.fitmentSkippedToastTimer = null;
            }, 4500);
        },

        dismissFitmentSkippedToast() {
            this.showFitmentSkippedToast = false;
            this.clearFitmentSkippedToastTimer();
        },

        dismissFitmentSkippedToastOnMaterialInteraction() {
            if (this.currentStep === 'material') {
                this.dismissFitmentSkippedToast();
            }
        },

        clearFitmentSkippedToastTimer() {
            if (!this.fitmentSkippedToastTimer) return;

            window.clearTimeout(this.fitmentSkippedToastTimer);
            this.fitmentSkippedToastTimer = null;
        },

        async handleDrawerOpen(event) {
            const detail = event?.detail || {};

            if (detail.changeVehicle) {
                this.resetFlow();
                this.seedVehicleSelection = {};
                this.vehicleKey++;
                return;
            }

            if (detail.startOver) {
                clearCurrentSelection(detail.selection);
                this.resetFlow();
                this.seedVehicleSelection = null;
                this.vehicleKey++;
                return;
            }

            if (!detail.selection?.vehicleParams || !detail.step) {
                return;
            }

            const step = detail.step;
            const selection = detail.selection;

            if (step === 'vehicle') {
                this.resetFlow();
                this.seedVehicleSelection = {...selection.vehicleParams};
                this.vehicleKey++;
                return;
            }

            await this.seedFromSelection(selection, step);
        },

        async seedFromSelection(selection, step) {
            this.error = null;
            this.loading = true;

            try {
                const params = {...selection.vehicleParams};
                const handle = selection.handle || slugify(Object.values(params).join('-'));
                const {hits} = await Searcher.search(this.isFoam() ? 'foam durofoam' : '*', {
                    optionalWords: this.isFoam() ? 'foam durofoam' : null,
                    filters: Searcher.buildFilters(params),
                    hitsPerPage: 2000
                });

                if (!hits.length) {
                    this.error = 'No matching products were found for that vehicle.';
                    this.currentStep = 'vehicle';
                    return;
                }

                this.vehicleParams = params;
                this.vehicleHandle = handle;
                this.results = hits.map(hit => new Result(hit, new Vehicle(hit.vehicle)));
                this.vehicle = this.results[0].vehicle;
                this.selections = filterSelectionsForStep(selection, step);
                this.ignored = [];
                this.seedVehicleSelection = params;
                this.preparedFilters = await this.buildPreparedFilters();
                this.fitmentBypassed = !this.hasConfigurableFitment(this.preparedFilters);
                this.fullConfigurationBypassed = this.fitmentBypassed
                    && !this.hasConfigurableMaterial(this.preparedFilters);
                this.materialBypassed = this.fullConfigurationBypassed
                    || (
                        step === 'material'
                        && !this.hasConfigurableMaterial(this.preparedFilters, this.selections)
                    );

                this.currentStep = (step === 'material' || this.fitmentBypassed) ? 'material' : 'fitment';
                this.filtersKey++;
                this.vehicleKey++;
            } finally {
                this.loading = false;
            }
        },

        setCurrentStep(step, data = {}) {
            if (step && this.currentStep !== 'vehicle') {
                if (data.selections) {
                    this.selections = {...data.selections};
                }

                this.currentStep = step;
                this.materialBypassed = step === 'material'
                    && !this.hasConfigurableMaterial(this.preparedFilters || [], this.selections);
            }
        },

        goBack() {
            if (this.currentStep === 'material') {
                if (this.fitmentBypassed) {
                    this.fitmentBypassed = false;
                    this.fullConfigurationBypassed = false;
                    this.materialBypassed = false;
                    this.dismissFitmentSkippedToast();
                    this.seedVehicleSelection = this.vehicleParams
                        ? {...this.vehicleParams}
                        : this.seedVehicleSelection;
                    this.currentStep = 'vehicle';
                    this.filtersKey++;
                    this.vehicleKey++;
                    return;
                }

                this.materialBypassed = false;
                this.currentStep = 'fitment';
                this.filtersKey++;
                return;
            }

            if (this.currentStep === 'fitment') {
                this.seedVehicleSelection = this.vehicleParams
                    ? {...this.vehicleParams}
                    : this.seedVehicleSelection;
                this.currentStep = 'vehicle';
                this.filtersKey++;
                this.vehicleKey++;
                return;
            }

            this.closeDrawer();
        },

        closeDrawer() {
            document.dispatchEvent(new CustomEvent('seatshop:vehicle-drawer-request-close'));
        },

        resetFlow() {
            this.currentStep = 'vehicle';
            this.error = null;
            this.filtersKey++;
            this.fitmentBypassed = false;
            this.fitmentBypassLoading = false;
            this.fullConfigurationBypassed = false;
            this.materialBypassed = false;
            this.ignored = [];
            this.loading = false;
            this.preparedFilters = null;
            this.results = [];
            this.seedVehicleSelection = null;
            this.selections = {};
            this.vehicle = null;
            this.vehicleHandle = null;
            this.vehicleParams = null;
            this.skippedFitmentTooltipOpen = false;
            this.skippedFitmentTooltipStyle = {};
            this.dismissFitmentSkippedToast();
        },

        viewProducts(data) {
            this.selections = data.selections || {};
            this.ignored = data.ignored || [];
            Storage.storeSelections(this.vehicleHandle, this.selections);
            storeCurrentSelection({
                vehicleParams: this.vehicleParams,
                selections: this.selections,
                standardSteps: this.fullConfigurationBypassed ? ['fitment', 'material'] : [],
                foam: this.isFoam(),
                handle: this.vehicleHandle
            });

            const queryString = new URLSearchParams({
                results: '1',
                ...this.vehicleParams,
                foam: this.isFoam()
            });

            Object.keys(this.selections).forEach(key => {
                const value = this.selections[key];
                if (![null, ''].includes(value)) {
                    queryString.set(key, value);
                }
            });

            window.location.href = `/collections/${this.vehicleHandle}/?${queryString.toString()}`;
        },

        isFoam() {
            if (this.foam === true || this.foam === 'true') {
                return true;
            }

            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('foam') == 'true' ? true : false;
        }
    }
};
</script>
